import React, { useEffect, useMemo, useState } from "react";
import { BarChart } from "@mui/x-charts";
import MonthRangeSelector from "../../../../components/pearli/MonthRangeSelector.jsx";
import api from "../../../../axiosconfig.js";
import {
  compareMonthKeys,
  getCurrentMonthKey,
  getRecentMonthOptions,
  getValidatedMonthRange,
  isSelectableMonthKey,
  monthKeyToLabel,
  monthKeyToRange,
  monthKeyToShortLabel,
} from "../../../../utils/dateUtils.js";

// Date helpers 
const startOfMonthLocal = (d) =>
  new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0, 0);
const endOfMonthLocal = (d) =>
  new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
const endOfDayLocal = (d) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);

const toIsoDate = (d) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// UI constants 
const DOMAINS = [
  "Physical",
  "Emotional",
  "Acceptance",
  "Relationships",
  "Loving-Kindness",
  "Intelligence",
];

const GREEN = "#19B880";
const GRAY = "#334155";
const RED = "#EF4444";

const DOMAIN_INDEX = {
  PHYSICAL: 0,
  EMOTIONAL: 1,
  ACCEPTANCE: 2,
  RELATIONSHIP: 3,
  RELATIONSHIPS: 3,
  "LOVING-KINDNESS": 4,
  "LOVING KINDNESS": 4,
  INTELLECTUAL: 5,
  INTELLIGENCE: 5,
};

const normalizeDomain = (value) =>
  String(value || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "-");

// Component 
const DomainEngagementOverview = () => {
  const [dataViewMode, setDataViewMode] = useState("current");

  // Calendar state (local)
  const today = useMemo(() => new Date(), []);
  const currentMonthKey = useMemo(() => getCurrentMonthKey(today), [today]);
  const defaultMonthRange = useMemo(
    () => getValidatedMonthRange({ anchorDate: today }),
    [today]
  );
  const monthOptions = useMemo(
    () => getRecentMonthOptions({ anchorDate: today, includeCurrentMonth: true }),
    [today]
  );

  const thisStart = useMemo(() => startOfMonthLocal(today), [today]);
  const thisEnd = useMemo(() => endOfDayLocal(today), [today]);

  const [rangeStartMonth, setRangeStartMonth] = useState(defaultMonthRange.startKey);
  const [rangeEndMonth, setRangeEndMonth] = useState(defaultMonthRange.endKey);

  // Data state
  const [prevCounts, setPrevCounts] = useState([0, 0, 0, 0, 0, 0]);
  const [currCounts, setCurrCounts] = useState([0, 0, 0, 0, 0, 0]);
  const [pearliLoad, setPearliLoad] = useState(false);

  // Labels 
  const displayDateString = useMemo(() => {
    if (dataViewMode === "current") return monthKeyToLabel(currentMonthKey);
    if (rangeStartMonth && rangeEndMonth)
      return `${monthKeyToLabel(rangeStartMonth)} – ${monthKeyToLabel(rangeEndMonth)}`;
    return "Select a month range";
  }, [currentMonthKey, dataViewMode, rangeEndMonth, rangeStartMonth]);

  const prevLabelShort = useMemo(
    () => (rangeStartMonth ? monthKeyToShortLabel(rangeStartMonth) : "Loading"),
    [rangeStartMonth]
  );
  const currLabelShort = useMemo(
    () =>
      dataViewMode === "current"
        ? monthKeyToShortLabel(currentMonthKey)
        : rangeEndMonth
          ? monthKeyToShortLabel(rangeEndMonth)
          : "Loading",
    [currentMonthKey, dataViewMode, rangeEndMonth]
  );

  // API 
  const toCountsArray = (domainCounts) => {
    const counts = [0, 0, 0, 0, 0, 0];
    (domainCounts || []).forEach((entry) => {
      const key = normalizeDomain(entry.answer_domain);
      const index = DOMAIN_INDEX[key];
      if (index === undefined) return;
      counts[index] = Number(entry.domain_count) || 0;
    });
    return counts;
  };

  const getMonthComparisonRange = (monthKey) => {
    const range = monthKeyToRange(monthKey);
    if (!range) return null;

    if (monthKey === currentMonthKey) {
      return {
        start: range.start,
        end: toIsoDate(today),
      };
    }

    return range;
  };

  const fetchSingleRangeCounts = async (startDate, endDate) => {
    const response = await api.get("/api/checkins/domain-counts", {
      params: {
        start_date: startDate,
        end_date: endDate,
      },
    });
    return toCountsArray(response?.data?.domain_counts);
  };

  const fetchPearliDistribution = async (
    startDate,
    endDate,
    startMonthKey,
    includeComparison = true
  ) => {
    setPearliLoad(false);
    try {
      const curr = await fetchSingleRangeCounts(startDate, endDate);
      let prev = [0, 0, 0, 0, 0, 0];

      if (includeComparison) {
        const compareRange = getMonthComparisonRange(startMonthKey);
        if (compareRange) {
          prev = await fetchSingleRangeCounts(compareRange.start, compareRange.end);
        }
      }

      setCurrCounts(curr);
      setPrevCounts(prev);
    } catch (e) {
      console.error(e);
      setCurrCounts([0, 0, 0, 0, 0, 0]);
      setPrevCounts([0, 0, 0, 0, 0, 0]);
    } finally {
      setPearliLoad(true);
    }
  };

  useEffect(() => {
    fetchPearliDistribution(
      toIsoDate(thisStart),
      toIsoDate(thisEnd),
      currentMonthKey,
      false
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handlers 
  const applyRangeSelection = (nextStartMonth, nextEndMonth) => {
    if (!nextStartMonth || !nextEndMonth) return;
    if (
      !isSelectableMonthKey(nextStartMonth, {
        anchorDate: today,
        maxMonthKey: currentMonthKey,
      }) ||
      !isSelectableMonthKey(nextEndMonth, {
        anchorDate: today,
        maxMonthKey: currentMonthKey,
      })
    ) {
      return;
    }
    if (compareMonthKeys(nextStartMonth, nextEndMonth) > 0) return;

    const startRange = monthKeyToRange(nextStartMonth);
    const endRange = monthKeyToRange(nextEndMonth);
    if (!startRange || !endRange) return;

    const currentMonthStart = `${currentMonthKey}-01`;
    const currentMonthEnd = toIsoDate(today);
    const rangeToStart =
      nextEndMonth === currentMonthKey ? currentMonthStart : endRange.start;
    const rangeToEnd = nextEndMonth === currentMonthKey ? currentMonthEnd : endRange.end;

    setRangeStartMonth(nextStartMonth);
    setRangeEndMonth(nextEndMonth);
    fetchPearliDistribution(
      rangeToStart,
      rangeToEnd,
      nextStartMonth,
      true
    );
  };

  const handleIntervalChange = (mode) => {
    setDataViewMode(mode);

    if (mode === "current") {
      fetchPearliDistribution(
        toIsoDate(thisStart),
        toIsoDate(thisEnd),
        currentMonthKey,
        false
      );
      return;
    }

    if (mode === "range") {
      applyRangeSelection(
        rangeStartMonth || defaultMonthRange.startKey,
        rangeEndMonth || defaultMonthRange.endKey
      );
    }
  };

  const handleStartMonthChange = (nextStartMonth) => {
    const nextEndMonth =
      rangeEndMonth && compareMonthKeys(nextStartMonth, rangeEndMonth) > 0
        ? nextStartMonth
        : rangeEndMonth;

    applyRangeSelection(nextStartMonth, nextEndMonth);
  };

  const handleEndMonthChange = (nextEndMonth) => {
    const nextStartMonth =
      rangeStartMonth && compareMonthKeys(rangeStartMonth, nextEndMonth) > 0
        ? nextEndMonth
        : rangeStartMonth;

    applyRangeSelection(nextStartMonth, nextEndMonth);
  };

  // Derived table rows 
  const totalPrev = prevCounts.reduce((a, b) => a + b, 0);
  const totalCurr = currCounts.reduce((a, b) => a + b, 0);

  const rows = DOMAINS.map((label, i) => {
    const prev = prevCounts[i] ?? 0;
    const curr = currCounts[i] ?? 0;
    const pctOfTotal =
      totalCurr === 0 ? "0.0%" : ((curr / totalCurr) * 100).toFixed(1) + "%";
    const changeAbs = curr - prev;
    const changePctOfPrev =
      prev === 0 ? "N/A" : (((curr - prev) / prev) * 100).toFixed(1) + "%";
    const color = changeAbs > 0 ? GREEN : changeAbs === 0 ? GRAY : RED;
    return {
      label: label === "Relationship" ? "Relationships" : label,
      prev,
      curr,
      pctOfTotal,
      changeAbs,
      changeColor: color,
      changePctOfPrev,
    };
  });

  return (
    <div className="home-statistics-group-container">
      <div className="home-statistics-section-container">
        <div className="home-statistics-section-header">
          <div className="home-statistics-section-text-container">
            <div className="home-statistics-section-title">
              Domain Engagement Overview ({displayDateString})
            </div>
            <div className="home-statistics-section-subtitle">
              Data reflects distribution of user selected primary PEARLI domain
              of focus
            </div>
          </div>

          <div className="home-menu-container">
            <div className="home-statistics-section-interval-button-container">
              <div
                className={`home-statistics-section-interval-button ${dataViewMode === "current" ? "active" : ""
                  }`}
                onClick={() => handleIntervalChange("current")}
              >
                Current Month
              </div>
              <div
                className={`home-statistics-section-interval-button ${dataViewMode === "range" ? "active" : ""
                  }`}
                onClick={() => handleIntervalChange("range")}
              >
                Month Range
              </div>
            </div>

            <div style={{ display: dataViewMode === "range" ? "flex" : "none" }}>
              <MonthRangeSelector
                monthOptions={monthOptions}
                startMonth={rangeStartMonth}
                endMonth={rangeEndMonth}
                onChangeStart={handleStartMonthChange}
                onChangeEnd={handleEndMonthChange}
                sx={{ mt: 0 }}
                anchorDate={today}
                allowCurrentMonth
              />
            </div>
          </div>
        </div>

        {/* Graphs */}
        {/* Graphs & Table Side-by-Side Grid */}
        <div className="home-statistics-section-item-container domain-overview-grid">
          <div className="home-statistics-section-item-w-bargraph">
            <div className="home-statistics-section-item-header">
              <div className="home-statistics-section-item-title">
                Domain Engagement Overview ({displayDateString})
              </div>
              <div className="home-statistics-section-item-subtitle">
                Data reflects distribution of user selected primary PEARLI domain of focus
              </div>
            </div>

            {/* Range comparison */}
            <div
              className="home-statistics-section-item-w-bargraph-body"
              id="pearlidistcomparison"
              style={{ display: dataViewMode === "range" ? "flex" : "none" }}
            >
              <div className="home-statistics-section-item-body-text" style={{ width: "100%" }}>
                <BarChart
                  loading={!pearliLoad}
                  xAxis={[{ scaleType: "band", data: DOMAINS }]}
                  yAxis={[{ label: "# of Selections", width: 50 }]}
                  series={[
                    { data: prevCounts, color: "#3B82F6", label: prevLabelShort },
                    { data: currCounts, color: "#0C8F63", label: currLabelShort },
                  ]}
                  height={320}
                />
              </div>
            </div>

            {/* Current-only */}
            <div
              className="home-statistics-section-item-w-bargraph-body"
              id="pearlidistsingle"
              style={{ display: dataViewMode === "current" ? "flex" : "none" }}
            >
              <div className="home-statistics-section-item-body-text" style={{ width: "100%" }}>
                <BarChart
                  loading={!pearliLoad}
                  xAxis={[{ scaleType: "band", data: DOMAINS }]}
                  yAxis={[{ label: "# of Selections", width: 50 }]}
                  series={[
                    {
                      data: currCounts,
                      color: "#3B82F6",
                      label: displayDateString,
                    },
                  ]}
                  height={320}
                />
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="home-statistics-section-item-table-container">
            <div className="home-statistics-section-item-header">
              <div className="home-statistics-section-item-title">
                Domain Engagement Summary ({displayDateString})
              </div>
              <div className="home-statistics-section-item-subtitle">
                Showing monthly clicks per domain with month comparison % change
              </div>
            </div>

            <div className="home-statistics-table-scroll-wrapper">
              {/* Table header */}
              <div
                className={`home-statistics-section-table-header-container ${
                  dataViewMode === "current" ? "table-current" : "table-range"
                }`}
              >
                <div className="home-statistics-section-table-headers-text col-domain">
                  Domain
                </div>
                {dataViewMode !== "current" && (
                  <div className="home-statistics-section-table-headers-text col-prev">
                    {prevLabelShort}
                  </div>
                )}
                <div className="home-statistics-section-table-headers-text col-curr">
                  {currLabelShort}
                </div>
                <div className="home-statistics-section-table-headers-text col-pct">
                  % of Total
                </div>
                {dataViewMode !== "current" && (
                  <>
                    <div className="home-statistics-section-table-headers-text col-change">
                      Change
                    </div>
                    <div className="home-statistics-section-table-headers-text col-changepct">
                      Change(%)
                    </div>
                  </>
                )}
              </div>

              {/* Data rows */}
              <div className="home-statistics-section-table-body-container">
                {rows.map((row) => (
                  <div
                    className={`home-statistics-section-table-row-container ${
                      dataViewMode === "current" ? "table-current" : "table-range"
                    }`}
                    key={row.label}
                  >
                    <div className="home-statistics-section-table-body-text col-domain">
                      {row.label}
                    </div>

                    {dataViewMode !== "current" && (
                      <div className="home-statistics-section-table-body-text col-prev">
                        {row.prev}
                      </div>
                    )}

                    <div className="home-statistics-section-table-body-text col-curr">
                      {row.curr}
                    </div>

                    <div className="home-statistics-section-table-body-text col-pct">
                      {row.pctOfTotal}
                    </div>

                    {dataViewMode !== "current" && (
                      <>
                        <div
                          className="home-statistics-section-table-body-text col-change"
                          style={{ color: row.changeColor }}
                        >
                          {row.changeAbs > 0 ? `+${row.changeAbs}` : `${row.changeAbs}`}
                        </div>

                        <div
                          className="home-statistics-section-table-body-text col-changepct"
                          style={{ color: row.changeColor }}
                        >
                          {row.changePctOfPrev}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>

              {/* Total row */}
              {(() => {
                const diff = totalCurr - totalPrev;
                const color = diff > 0 ? GREEN : diff === 0 ? GRAY : RED;
                const signVal = diff > 0 ? `+${diff}` : `${diff}`;
                const pct =
                  totalPrev === 0
                    ? "N/A"
                    : (((totalCurr - totalPrev) / totalPrev) * 100).toFixed(1) + "%";
                return (
                  <div
                    className={`home-statistics-section-table-row-container home-statistics-section-table-summary-row ${
                      dataViewMode === "current" ? "table-current" : "table-range"
                    }`}
                  >
                    <div className="home-statistics-section-table-body-text-total col-domain">
                      Total
                    </div>
                    {dataViewMode !== "current" && (
                      <div className="home-statistics-section-table-body-text-total col-prev">
                        {totalPrev}
                      </div>
                    )}
                    <div className="home-statistics-section-table-body-text-total col-curr">
                      {totalCurr}
                    </div>
                    <div className="home-statistics-section-table-body-text-total col-pct">
                      {totalCurr > 0 ? "100%" : "0.0%"}
                    </div>
                    {dataViewMode !== "current" && (
                      <>
                        <div
                          className="home-statistics-section-table-body-text-total col-change"
                          style={{ color }}
                        >
                          {signVal}
                        </div>
                        <div
                          className="home-statistics-section-table-body-text-total col-changepct"
                          style={{ color }}
                        >
                          {pct}
                        </div>
                      </>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DomainEngagementOverview;
